import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { memo, useCallback, useContext, useState } from 'react';

import Address from '@/components/Address';
import InPlaceEditor from '@/components/PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import { Field, FieldTypes } from '@/components/PersonInPlaceEditor/types';
import ValidateAddress from '@/components/ValidateAddress';
import { DEFAULT_COUNTRY_US } from '@/constants/address';
import AppContext from '@/contexts/appContext';
import { useAddressTypes } from '@/hooks/useAddressTypes';
import useUsStates from '@/hooks/useUsStates';
import PersonContext from '@/pages/Person/PersonContext';
import { addressesSchema } from '@/pages/Profile/schemas';
import AddressRepository from '@/repositories/AddressRepository';
import { AddressType, ValidatedAddress } from '@/types/address';
import type { Address as AddressInterface } from '@/types/person';
import DataList, { DataRow } from '@/ui/components/DataList';
import Heading from '@/ui/components/Heading';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const AddressSection = () => {
  const { person, refetch: refetchPerson } = useContext(PersonContext);
  const addressRepository = AddressRepository();
  const { addMessage } = useContext(AppContext);

  const [showValidatedAddressModal, setShowValidatedAddressModal] = useState<{
    showModal: boolean;
    values: AddressInterface | null;
  }>({ showModal: false, values: null });
  const [resolveValidatedAddressPromise, setResolveValidatedAddressPromise] = useState<
    ((value: ValidatedAddress) => void) | null
  >(null);

  const hasAddresses = !!person.addresses.length;
  const defaultAddress = person.addresses.find((address) => address.default);

  const { refetch: getUsStates } = useUsStates(false);
  const formatStates = useCallback(async () => {
    const { data } = await getUsStates();
    if (data) return data.map(({ name }) => ({ label: name, value: name }));
    return [];
  }, [getUsStates]);

  const { refetch: getAddressTypes } = useAddressTypes(false);
  const formatAddressTypes = useCallback(async () => {
    const { data } = await getAddressTypes();
    if (data?.results) return data.results.map(({ id, name }) => ({ label: name, value: id }));
    return [];
  }, [getAddressTypes]);

  const fields = (personIdField: boolean): Field<AddressInterface>[] => [
    {
      key: 'id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      alwaysSend: personIdField,
      key: 'person_id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      key: 'default',
      label: 'Default',
      type: FieldTypes.BOOLEAN
    },
    {
      key: 'address_type_id',
      label: 'Address Type',
      options: formatAddressTypes,
      type: FieldTypes.SELECT
    },
    {
      key: 'first_name',
      label: 'First Name',
      type: FieldTypes.TEXT
    },
    {
      key: 'last_name',
      label: 'Last Name',
      type: FieldTypes.TEXT
    },
    {
      key: 'country',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      key: 'address1',
      label: 'Address (Line 1 - Street Address)',
      type: FieldTypes.TEXT
    },
    {
      key: 'address2',
      label: 'Address (Line 2 - Apt, Suite, etc.)',
      type: FieldTypes.TEXT
    },
    {
      key: 'city',
      label: 'City',
      type: FieldTypes.TEXT
    },
    {
      key: 'state',
      label: 'State',
      type: FieldTypes.SELECT,
      options: formatStates
    },
    {
      key: 'postal_code',
      label: 'Postal Code',
      type: FieldTypes.TEXT
    },
    {
      key: 'mailing_instructions',
      label: 'Mailing Instructions',
      type: FieldTypes.TEXTAREA
    }
  ];

  const mutateAddress = useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: Partial<AddressInterface> }) => {
      if (!Object.keys(values).length) {
        return;
      }
      if (!id) {
        return await addressRepository.create({ data: values });
      }
      return await addressRepository.update({ id, data: values });
    },
    onSuccess: async (response) => {
      if (!response) return;
      await refetchPerson();
      addMessage('success', 'Address saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deleteAddress = useMutation({
    mutationFn: (id: string) => addressRepository.delete({ id }),
    onSuccess: async () => {
      await refetchPerson();
      addMessage('success', 'Address deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const submittedValuesManipulator = async (values: Partial<AddressInterface>) => {
    setShowValidatedAddressModal({ ...showValidatedAddressModal, showModal: true, values });

    const selectedValidatedAddress = await new Promise<ValidatedAddress>((resolve) => {
      setResolveValidatedAddressPromise(() => resolve);
    });
    const fieldsNotTrackedInValidatedAddresses = [
      'address_type_id',
      'default',
      'first_name',
      'last_name',
      'mailing_instructions'
    ];
    const originalFieldsToKeep = Object.entries(values).reduce((acc, [key, value]) => {
      if (fieldsNotTrackedInValidatedAddresses.includes(key)) {
        (acc as { [key: string]: string | boolean | AddressType | null })[key] = value;
      }

      return acc;
    }, {});

    return {
      ...selectedValidatedAddress,
      ...originalFieldsToKeep,
      person_id: person.id
    };
  };

  const handleSelect = (value: ValidatedAddress) => {
    if (resolveValidatedAddressPromise) {
      resolveValidatedAddressPromise?.(value);
      setShowValidatedAddressModal({ ...showValidatedAddressModal, showModal: false });
    }
  };

  return (
    <div id="contact-info-section-addresses-row">
      <Heading className={`${hasAddresses ? 'mb-2' : ''}`} value={4} text="Address" />

      <DataList>
        {hasAddresses ? (
          <>
            {person.addresses.map((address, idx) => (
              <div key={address.id} className="group/subsection mb-4 relative">
                <InPlaceEditor
                  key={address.id}
                  deleteMutation={deleteAddress}
                  editButtonClass="top-3"
                  mutation={mutateAddress}
                  record={{
                    ...address,
                    person_id: person.id
                  }}
                  sendAllFields
                  submittedValuesManipulator={submittedValuesManipulator}
                  subsectionEditor
                  schema={addressesSchema}
                  fields={fields(true)}>
                  <DataRow
                    key={`member-address-${address.id}`}
                    label={`#${idx + 1} ${address.default ? '(Default)' : ''}`}>
                    <Address address={address} />
                  </DataRow>
                </InPlaceEditor>
              </div>
            ))}
          </>
        ) : (
          <DataRow label="None" />
        )}
      </DataList>

      <div className="group/subsection relative mt-3">
        <InPlaceEditor
          fields={fields(true)}
          mutation={mutateAddress}
          newRecord
          newRecordButtonText={'Add New (blank)'}
          record={{
            country: DEFAULT_COUNTRY_US,
            default: !hasAddresses,
            first_name: person.first_name,
            last_name: person.last_name,
            person_id: person.id
          }}
          schema={addressesSchema}
          sendAllFields
          submittedValuesManipulator={submittedValuesManipulator}
          subsectionEditor
        />
      </div>

      {hasAddresses && defaultAddress && (
        <div className="group/subsection relative mt-3">
          <InPlaceEditor
            fields={fields(true)}
            mutation={mutateAddress}
            newRecord
            newRecordButtonText={'Add New (same as default)'}
            record={{
              ...defaultAddress,
              id: undefined,
              address_type_id: defaultAddress.address_type?.id,
              country: DEFAULT_COUNTRY_US,
              default: false,
              first_name: person.first_name,
              last_name: person.last_name,
              person_id: person.id
            }}
            sendAllFields
            schema={addressesSchema}
            submittedValuesManipulator={submittedValuesManipulator}
            subsectionEditor
          />
        </div>
      )}

      {showValidatedAddressModal.showModal && showValidatedAddressModal.values && (
        <ValidateAddress
          address={showValidatedAddressModal.values}
          onModalClose={() =>
            setShowValidatedAddressModal({ ...showValidatedAddressModal, showModal: false })
          }
          onSelect={handleSelect}
        />
      )}
    </div>
  );
};

export default memo(AddressSection);
